import { Bot } from "lucide-react"
import * as React from "react"
import { Link, Navigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { LanguageSwitcher } from "@/components/language-switcher"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useT } from "@/i18n/provider"
import { api, ApiError } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { renderGoogleButton } from "@/lib/google"
import type { AuthConfig } from "@/lib/types"

export function LoginPage() {
  const t = useT()
  const { user, signIn, signInWithGoogle, signUp, verifyRegistration } = useAuth()
  const [searchParams] = useSearchParams()
  // Where to land after auth (e.g. back at /invite/:token). Only a genuine
  // internal absolute path is honored - a leading "/" whose second character
  // is neither "/" nor "\" - so this can never become an open redirect (both
  // "//evil.com" and the backslash form "/\evil.com" are rejected; browsers
  // and react-router treat a leading backslash as a path separator too).
  const redirectParam = searchParams.get("redirect")
  const redirectTo =
    redirectParam && /^\/(?!\/|\\)/.test(redirectParam) ? redirectParam : "/chat"
  const [mode, setMode] = React.useState("signin")
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [code, setCode] = React.useState("")
  const [signupStep, setSignupStep] = React.useState<"form" | "code">("form")
  const [busy, setBusy] = React.useState(false)
  const [config, setConfig] = React.useState<AuthConfig | null>(null)
  const [googleReady, setGoogleReady] = React.useState(false)
  const googleRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    let cancelled = false
    api
      .authConfig()
      .then((cfg) => {
        if (cancelled) return
        setConfig(cfg)
        if (!cfg.google_enabled || !cfg.google_client_id || !googleRef.current) return
        googleRef.current.replaceChildren()
        return renderGoogleButton(googleRef.current, cfg.google_client_id, (credential) => {
          signInWithGoogle(credential).catch((error) => {
            toast.error(error instanceof ApiError ? error.message : t("login.toasts.googleFailed"))
          })
        }).then(() => {
          if (!cancelled) setGoogleReady(true)
        })
      })
      .catch(() => {
        /* config unavailable: the password form still works */
      })
    return () => {
      cancelled = true
    }
  }, [signInWithGoogle, t])

  if (user) return <Navigate to={redirectTo} replace />

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      if (mode === "signin") {
        await signIn(email, password)
      } else if (config?.email_verification_required) {
        if (signupStep === "form") {
          await api.registerStart(email, password)
          setSignupStep("code")
          toast.success(t("login.toasts.codeSent"))
        } else {
          await verifyRegistration(email, code)
        }
      } else {
        await signUp(email, password)
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("login.toasts.genericError"))
    } finally {
      setBusy(false)
    }
  }

  async function resend() {
    try {
      await api.registerStart(email, password)
      toast.success(t("login.toasts.codeResent"))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("login.toasts.resendFailed"))
    }
  }

  const showCodeStep = mode === "signup" && config?.email_verification_required && signupStep === "code"

  return (
    <div className="bg-background flex min-h-full flex-col items-center justify-center p-6">
      <div className="mb-4 flex w-full max-w-sm justify-end">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="bg-primary text-primary-foreground mx-auto mb-2 flex size-10 items-center justify-center rounded-xl">
            <Bot className="size-5" />
          </div>
          <CardTitle>AI Agents</CardTitle>
          <CardDescription>{t("login.tagline")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={mode}
            onValueChange={(value) => {
              setMode(value)
              setSignupStep("form")
              setCode("")
            }}
          >
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="signin">{t("login.tabs.signIn")}</TabsTrigger>
              <TabsTrigger value="signup">{t("login.tabs.signUp")}</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="space-y-4">
              {!showCodeStep && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("login.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t("login.password")}</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete={mode === "signin" ? "current-password" : "new-password"}
                      required
                      minLength={8}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={mode === "signup" ? t("login.passwordHint") : ""}
                    />
                  </div>
                </>
              )}

              {showCodeStep && (
                <div className="space-y-2">
                  <Label htmlFor="code">{t("login.codeLabel")}</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder={t("login.codePlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={resend}
                    className="text-muted-foreground hover:text-foreground text-xs underline"
                  >
                    {t("login.resend")}
                  </button>
                </div>
              )}

              <TabsContent value="signin" className="m-0" />
              <TabsContent value="signup" className="m-0" />
              <Button type="submit" className="w-full" disabled={busy}>
                {busy
                  ? t("login.submit.wait")
                  : mode === "signin"
                    ? t("login.submit.signIn")
                    : showCodeStep
                      ? t("login.submit.verify")
                      : config?.email_verification_required
                        ? t("login.submit.sendCode")
                        : t("login.submit.signUp")}
              </Button>
            </form>
          </Tabs>

          {mode === "signin" && (
            <div className="mt-3 text-center">
              <Link to="/reset-password" className="text-muted-foreground hover:text-foreground text-xs underline">
                {t("login.forgot")}
              </Link>
            </div>
          )}

          <div hidden={!googleReady}>
            <div className="my-4 flex items-center gap-3">
              <div className="bg-border h-px flex-1" />
              <span className="text-muted-foreground text-xs uppercase">{t("login.or")}</span>
              <div className="bg-border h-px flex-1" />
            </div>
            <div ref={googleRef} className="flex justify-center" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
